import type { JobTemplateExportResult } from '../shared/desktop-api';

/**
 * What a job setup file carried, said in the words the screens use.
 *
 * Download job setup and Saved job setup both end with this tally, so an operator
 * moving a job to another workstation can tell what travelled. It speaks the Lookup
 * lists vocabulary (docs/product/interface-principles.md): a list is the file someone
 * adds, its rows are rows, and a lookup is a saved lookup rule (normally a field
 * reading from a list). Lists and lookups are named only when the job has any: a job
 * whose list no field used yet was reported as "0 lookups, 12 table rows", which read
 * as the list left behind when the file held it with every row. Counts are written
 * with toLocaleString, as the Lookup lists screen writes them ("1,500 rows").
 */
export type JobSetupTallyCounts = Pick<
  JobTemplateExportResult,
  'documentTypeCount' | 'fieldCount' | 'lookupRuleCount' | 'dataTableCount' | 'dataTableRowCount'
>;

const counted = (count: number, singular: string) =>
  `${count.toLocaleString()} ${count === 1 ? singular : `${singular}s`}`;

/** "5 document types, 38 fields, 1 list (12 rows), 1 lookup", without a final stop. */
export const jobSetupTally = (counts: JobSetupTallyCounts): string => {
  const parts = [
    counted(counts.documentTypeCount, 'document type'),
    counted(counts.fieldCount, 'field'),
  ];
  if (counts.dataTableCount > 0)
    parts.push(
      `${counted(counts.dataTableCount, 'list')} (${counted(counts.dataTableRowCount, 'row')})`,
    );
  if (counts.lookupRuleCount > 0) parts.push(counted(counts.lookupRuleCount, 'lookup'));
  return parts.join(', ');
};
